/**
 * @fileoverview V2 Notes Step — Intake notes and general notes.
 *
 * Self-contained. Saves notes as staff notes via useIngestionStaffNote.
 * Not audit-tracked.
 */
import React, { useCallback, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { useIngestionStaffNote } from '@/components/case-ingestion/hooks/useIngestionStaffNote';

import {
  FormError,
  FormField,
  FormGroup,
  FormLabel,
  FormSection,
  FormTextarea,
} from '../components/form';
import { useStepHydration } from '../hooks/useStepHydration';
import { useIngestion } from '../state/IngestionProvider';

// ── Schema ───────────────────────────────────────────────────────────────────

const notesSchema = z.object({
  intake_notes: z.string().optional(),
  general_notes: z.string().optional(),
});

type NotesFormValues = z.infer<typeof notesSchema>;

// ── Component ────────────────────────────────────────────────────────────────

export const NotesStep: React.FC = () => {
  const { state, dispatch, registerSave, importData } = useIngestion();
  const matterId = state.matterId!;

  const { existingData: staffNoteData, isLoading: isLoadingNotes, saveIngestionNotes } =
    useIngestionStaffNote(matterId);

  // ── Hydration ──────────────────────────────────────────────────────────
  const saved = useMemo(() => {
    if (isLoadingNotes) return null;
    return {
      intake_notes: staffNoteData?.intakeNotes || '',
      general_notes: staffNoteData?.generalNotes || '',
    };
  }, [staffNoteData, isLoadingNotes]);

  // Import: source_notes → intake_notes, per-step notes → general_notes
  const imported = useMemo(() => {
    if (!importData?.payload) return null;
    const p = importData.payload;

    // Collect all section-level notes from import into general notes
    const allNotes: string[] = [];

    // Incident summary
    const incidentSummary = p.case_info?.incident_summary;
    if (incidentSummary?.presence === 'present' && incidentSummary.value)
      allNotes.push(`--- Incident Summary ---\n${incidentSummary.value}`);

    // Liability notes
    const liabNotes = p.liability?.liability_notes;
    if (liabNotes?.presence === 'present' && liabNotes.value)
      allNotes.push(`--- Liability Notes ---\n${liabNotes.value}`);

    // Treatment notes
    const treatNotes = p.treatment?.treatment_notes;
    if (treatNotes?.presence === 'present' && treatNotes.value)
      allNotes.push(`--- Treatment Notes ---\n${treatNotes.value}`);

    // Coverage notes
    const covNotes = p.coverage?.coverage_notes;
    if (covNotes?.presence === 'present' && covNotes.value)
      allNotes.push(`--- Coverage Notes ---\n${covNotes.value}`);

    // Source notes (general CMS notes)
    if (p.source_notes)
      allNotes.push(`--- Source Notes ---\n${p.source_notes}`);

    return {
      intake_notes: p.source_notes
        ? { presence: 'present' as const, value: p.source_notes }
        : { presence: 'missing' as const, value: null },
      general_notes: allNotes.length > 0
        ? { presence: 'present' as const, value: allNotes.join('\n\n') }
        : { presence: 'missing' as const, value: null },
    };
  }, [importData]);

  const { form, isAssisted } = useStepHydration<NotesFormValues>({
    schema: notesSchema,
    defaults: { intake_notes: '', general_notes: '' },
    saved,
    imported,
  });

  const { register, handleSubmit, formState: { errors, isSubmitting } } = form;

  // ── Save ───────────────────────────────────────────────────────────────
  const saveForm = useCallback(async (data: NotesFormValues): Promise<boolean> => {
    const hasIntake = !!data.intake_notes?.trim();
    const hasGeneral = !!data.general_notes?.trim();

    try {
      if (hasIntake || hasGeneral) {
        await saveIngestionNotes({
          intakeNotes: data.intake_notes?.trim() || '',
          generalNotes: data.general_notes?.trim() || '',
        });
      }
      toast.success('Notes saved');
      return true;
    } catch {
      toast.error('Failed to save notes');
      return false;
    }
  }, [saveIngestionNotes]);

  useEffect(() => {
    registerSave(async () => {
      let result = false;
      await handleSubmit(
        async (data) => { result = await saveForm(data); },
        () => { result = false; },
      )();
      return result;
    });
    return () => registerSave(null);
  }, [registerSave, handleSubmit, saveForm]);

  const onSubmit = async (data: NotesFormValues) => {
    const success = await saveForm(data);
    if (success) dispatch({ type: 'GO_TO_STEP', index: state.stepIndex + 1 });
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <FormSection title="Notes" description="Intake and general notes for this matter">
        <FormGroup>
          <FormField assisted={isAssisted('intake_notes')}>
            <FormLabel htmlFor="v2-intake-notes">Intake Notes</FormLabel>
            <FormTextarea
              id="v2-intake-notes"
              rows={8}
              placeholder="Notes from the intake call or initial consultation..."
              disabled={isSubmitting}
              {...register('intake_notes')}
            />
            <FormError>{errors.intake_notes?.message}</FormError>
          </FormField>

          <FormField assisted={isAssisted('general_notes')}>
            <FormLabel htmlFor="v2-general-notes">General Notes</FormLabel>
            <FormTextarea
              id="v2-general-notes"
              rows={8}
              placeholder="Any additional notes, reminders, or follow-up items..."
              disabled={isSubmitting}
              {...register('general_notes')}
            />
            <FormError>{errors.general_notes?.message}</FormError>
          </FormField>
        </FormGroup>
      </FormSection>

      <button
        type="submit"
        disabled={isSubmitting}
        className={
          'inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold ' +
          'bg-blue-600 text-white shadow-sm hover:bg-blue-700 ' +
          'disabled:opacity-50 disabled:cursor-not-allowed ' +
          'transition-colors duration-150'
        }
      >
        {isSubmitting ? 'Saving...' : 'Save & Continue'}
      </button>
    </form>
  );
};
